import { Box, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AxleOrgSyncOrgFragment,
  useAxleOrgSyncJobStatusesQuery,
  useAxleOrgSyncOrgsConnectionQuery,
  useCreateAxleOrgSyncJobMutation,
  useRetryAxleOrgSyncFailedBatchMutation,
} from "../../../../types";
import { OrgsListSynced, OrgsListUnlinked } from "../../../../views/Staff/Orgs/Sync/Orgs/Axle";
import ListsColumn from "../../../../views/Staff/Orgs/Sync/shared/ListColumn";

const isInFlightStatus = (status: string | null | undefined): boolean => status === "pending" || status === "inProgress";

const hasActiveSync = (orgs: readonly { latestAxleOrgSyncJob?: { status: string } | null }[]): boolean =>
  orgs.some(org => isInFlightStatus(org.latestAxleOrgSyncJob?.status));

const PAGE_SIZE = 25;

/**
 * One paginated column of Nutrition orgs from `orgOffsetConnection`.
 *
 * This page never loads the Hub/Axle org list: linked state lives on the
 * Nutrition org row (`axleOrgDocumentId`, set when the org is created in /
 * linked to Axle), so the server filters and searches Nutrition orgs and the
 * client only ever renders one page at a time. Previously the page fetched
 * every org plus every team's/membership's/athlete's id just to render
 * counts, which is why initial load was so slow.
 */
const useAxleOrgList = (axleLinked: boolean, searchQuery: string) => {
  const filter = useMemo(() => ({ isActive: null, axleLinked }), [axleLinked]);
  const { data, loading, fetchMore, refetch } = useAxleOrgSyncOrgsConnectionQuery({
    variables: {
      input: { offset: 0, limit: PAGE_SIZE },
      query: searchQuery || null,
      filter,
    },
  });

  const orgs = data?.orgOffsetConnection.edges ?? [];
  const pageInfo = data?.orgOffsetConnection.pageInfo;
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const previousSearchQueryRef = useRef(searchQuery);

  // Bump only after this search commits. A render React discards must not
  // invalidate a fetchMore that still belongs to the search on screen.
  useEffect(() => {
    if (previousSearchQueryRef.current === searchQuery) {
      return;
    }
    previousSearchQueryRef.current = searchQuery;
    requestGenerationRef.current += 1;
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }, [searchQuery]);

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage || loadingMoreRef.current) {
      return;
    }
    const requestGeneration = requestGenerationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    void fetchMore({
      variables: {
        input: { offset: orgs.length, limit: PAGE_SIZE },
        query: searchQuery || null,
        filter,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (requestGenerationRef.current !== requestGeneration) {
          return prev;
        }
        if (!fetchMoreResult) {
          return prev;
        }
        const prevEdges = prev.orgOffsetConnection.edges;
        const seenIds = new Set(prevEdges.map(org => org.id));
        const newEdges = fetchMoreResult.orgOffsetConnection.edges.filter(org => !seenIds.has(org.id));
        return {
          ...fetchMoreResult,
          orgOffsetConnection: {
            ...fetchMoreResult.orgOffsetConnection,
            edges: [...prevEdges, ...newEdges],
          },
        };
      },
    })
      .catch(() => undefined)
      .finally(() => {
        if (requestGenerationRef.current !== requestGeneration) {
          return;
        }
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [fetchMore, filter, orgs.length, pageInfo?.hasNextPage, searchQuery]);

  return {
    orgs,
    loading,
    loadingMore,
    loadMore,
    refetch,
    total: pageInfo?.total,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
};

export const AxleOrgsSyncPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedNutritionOrg, setSelectedNutritionOrg] = useState<AxleOrgSyncOrgFragment | null>(null);
  const [retryingBatchIndex, setRetryingBatchIndex] = useState<number | null>(null);
  const [createAxleOrgSyncJob] = useCreateAxleOrgSyncJobMutation();
  const [retryAxleOrgSyncFailedBatch] = useRetryAxleOrgSyncFailedBatchMutation();

  // Search is server-side now (name / Axle document id), so debounce keystrokes.
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const syncedList = useAxleOrgList(true, debouncedSearchTerm);
  const unlinkedList = useAxleOrgList(false, debouncedSearchTerm);

  // Start in the polling state so that opening the page mid-sync picks up the
  // running job; the effect below stops polling once a status result shows
  // nothing is active. The status query is slim (org id + sync job only) —
  // unlike the org lists it does not pull teams/memberships/athletes.
  const [pollStatuses, setPollStatuses] = useState(true);

  const { data: statusData, refetch: refetchStatuses } = useAxleOrgSyncJobStatusesQuery({
    skip: !pollStatuses,
  });

  // Poll explicitly with refetch instead of Apollo's pollInterval option.
  // Toggling `skip` together with a dynamic `pollInterval` (Apollo 3.3) can
  // leave the status query mounted but not actually polling, so the chip
  // stays on its initial counts until a full page refresh remounts it.
  // Unskipping the query triggers the first fetch; this interval drives the
  // rest and keeps retrying even if an individual poll errors.
  useEffect(() => {
    if (!pollStatuses) {
      return;
    }
    const intervalId = setInterval(() => {
      void refetchStatuses().catch(() => undefined);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [pollStatuses, refetchStatuses]);

  const applyFreshStatus = useCallback(
    (orgs: readonly AxleOrgSyncOrgFragment[]): readonly AxleOrgSyncOrgFragment[] => {
      const statusByOrgId = new Map((statusData?.orgs ?? []).map(org => [org.id, org.latestAxleOrgSyncJob] as const));
      return orgs.map(org => {
        if (!statusByOrgId.has(org.id)) {
          return org;
        }
        return {
          ...org,
          latestAxleOrgSyncJob: statusByOrgId.get(org.id) ?? null,
        };
      });
    },
    [statusData]
  );

  const syncedOrgsWithFreshStatus = useMemo(() => applyFreshStatus(syncedList.orgs), [applyFreshStatus, syncedList.orgs]);
  const unlinkedOrgsWithFreshStatus = useMemo(() => applyFreshStatus(unlinkedList.orgs), [applyFreshStatus, unlinkedList.orgs]);

  useEffect(() => {
    if (!pollStatuses) {
      return;
    }
    // Wait for the first status result before deciding jobs are done. The
    // status query covers every org, so it also catches syncs for orgs that
    // have not been scrolled into either list yet.
    if (!statusData) {
      return;
    }
    if (!hasActiveSync(statusData.orgs)) {
      setPollStatuses(false);
    }
  }, [pollStatuses, statusData]);

  const handleNutritionOrgLinkClick = (org: AxleOrgSyncOrgFragment) => {
    setSelectedNutritionOrg(org);
  };

  const retryFailedBatch = async (org: AxleOrgSyncOrgFragment, batchIndex: number) => {
    if (!org.axleOrgDocumentId) {
      console.error("Cannot retry batch - no axleOrgDocumentId");
      return;
    }

    setRetryingBatchIndex(batchIndex);
    try {
      await retryAxleOrgSyncFailedBatch({
        variables: {
          input: {
            axleOrgDocumentId: org.axleOrgDocumentId,
            batchIndex,
          },
        },
      });
      setPollStatuses(true);
      void refetchStatuses().catch(() => undefined);
      await syncedList.refetch();
    } catch (error) {
      console.error("Failed to retry Axle org sync batch:", error);
    } finally {
      setRetryingBatchIndex(null);
    }
  };

  const syncNutritionOrg = async (org: AxleOrgSyncOrgFragment) => {
    if (!org.axleOrgDocumentId) {
      console.error("Cannot sync org - no axleOrgDocumentId");
      return;
    }

    try {
      await createAxleOrgSyncJob({
        variables: {
          input: {
            axleOrgDocumentId: org.axleOrgDocumentId,
          },
        },
      });
      setPollStatuses(true);
      void refetchStatuses().catch(() => undefined);
      await syncedList.refetch();
    } catch (error) {
      console.error("Failed to create sync job:", error);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%", overflow: "hidden", flex: 1 }}>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", alignItems: "flex-end" }}>
        <TextField sx={{ width: 350 }} label="Filter Orgs" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          height: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <ListsColumn>
          <OrgsListUnlinked
            selectedId={selectedNutritionOrg?.id ?? null}
            orgs={unlinkedOrgsWithFreshStatus}
            loading={unlinkedList.loading}
            total={unlinkedList.total}
            hasNextPage={unlinkedList.hasNextPage}
            onLoadMore={unlinkedList.loadMore}
            loadingMore={unlinkedList.loadingMore}
            onLink={handleNutritionOrgLinkClick}
          />
        </ListsColumn>
        <ListsColumn>
          <OrgsListSynced
            selectedId={selectedNutritionOrg?.id ?? null}
            orgs={syncedOrgsWithFreshStatus}
            loading={syncedList.loading}
            total={syncedList.total}
            hasNextPage={syncedList.hasNextPage}
            onLoadMore={syncedList.loadMore}
            loadingMore={syncedList.loadingMore}
            onSync={syncNutritionOrg}
            onRetryBatch={retryFailedBatch}
            retryingBatchIndex={retryingBatchIndex}
          />
        </ListsColumn>
      </Box>
    </Box>
  );
};
